import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  makeStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SettingsIcon from '@material-ui/icons/Settings';
import { Lambda } from '../../LambdaListEditor';

const useStyles = makeStyles(theme => ({
  additionalSettingsSection: {
    marginTop: theme.spacing(3),
    '&:before': {
      display: 'none',
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    minHeight: 48,
    '&.Mui-expanded': {
      minHeight: 48,
    },
  },
  accordionDetails: {
    flexDirection: 'column',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  },
  icon: {
    fontSize: '1.25rem',
    color: theme.palette.text.secondary,
  },
  sectionDescription: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  fieldsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

interface LambdaAdditionalSettingsSectionProps {
  /**
   * The lambda object being edited
   */
  lambda: Lambda;
  /**
   * Callback when lambda_trigger_path changes
   */
  onLambdaTriggerPathChange: (value: string) => void;
  /**
   * Callback when dead_letter_queue changes
   */
  onDeadLetterQueueChange: (value: string) => void;
  /**
   * Callback when use_readonly_proxy changes
   */
  onUseReadonlyProxyChange: (value: boolean) => void;
}

/**
 * Additional settings section for lambda-specific configuration
 */
export const LambdaAdditionalSettingsSection = ({
  lambda,
  onLambdaTriggerPathChange,
  onDeadLetterQueueChange,
  onUseReadonlyProxyChange,
}: LambdaAdditionalSettingsSectionProps) => {
  const classes = useStyles();

  return (
    <Accordion className={classes.additionalSettingsSection} defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
        <div className={classes.titleContainer}>
          <SettingsIcon className={classes.icon} />
          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Additional Settings
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        <Typography variant="body2" className={classes.sectionDescription}>
          Configure additional lambda-specific settings
        </Typography>

        <div className={classes.fieldsContainer}>
          <Grid container spacing={2}>
            {/* Lambda Trigger Path */}
            <Grid item xs={12}>
              <TextField
                label="Lambda Trigger Path"
                value={lambda.lambda_trigger_path || ''}
                onChange={e => onLambdaTriggerPathChange(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="e.g., /api/v1/users"
                helperText="API Gateway path that triggers this lambda"
              />
            </Grid>

            {/* Dead Letter Queue */}
            <Grid item xs={12}>
              <TextField
                label="Dead Letter Queue"
                value={lambda.dead_letter_queue || ''}
                onChange={e => onDeadLetterQueueChange(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="e.g., my-dlq-name"
                helperText="Name of the dead letter queue for failed invocations"
              />
            </Grid>

            {/* Use Read-Only Proxy */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={lambda.use_readonly_proxy ?? false}
                    onChange={e => onUseReadonlyProxyChange(e.target.checked)}
                    color="primary"
                  />
                }
                label="Use Read-Only Proxy"
              />
            </Grid>
          </Grid>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
